/**
 * Generate a device fingerprint based on browser characteristics.
 * This provides a reasonably unique identifier for the device/browser combination.
 */

const getDeviceFingerprint = async () => {
  const components = [];

  // Screen properties
  components.push(window.screen.width);
  components.push(window.screen.height);
  components.push(window.screen.colorDepth);
  components.push(window.screen.pixelDepth);

  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Language
  components.push(navigator.language);
  components.push(navigator.languages?.join(',') || '');

  // Platform
  components.push(navigator.platform);

  // User Agent
  components.push(navigator.userAgent);

  // Hardware concurrency (CPU cores)
  components.push(navigator.hardwareConcurrency || 'unknown');

  // Device memory (if available)
  components.push(navigator.deviceMemory || 'unknown');

  // Touch support
  components.push('ontouchstart' in window ? 'touch' : 'no-touch');

  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Device fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Device fingerprint', 4, 17);
    components.push(canvas.toDataURL());
  } catch (e) {
    components.push('canvas-error');
  }

  // WebGL fingerprint
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }
  } catch (e) {
    components.push('webgl-error');
  }

  // Create a hash of all components
  const fingerprintString = components.join('|');
  const hash = await hashString(fingerprintString);

  return hash;
};

/**
 * Create a SHA-256 hash of a string
 */
const hashString = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

/**
 * Get device name for display purposes
 */
const getDeviceName = () => {
  const ua = navigator.userAgent;
  let deviceName = 'Unknown Device';

  // Detect browser
  if (ua.includes('Chrome')) {
    deviceName = 'Chrome';
  } else if (ua.includes('Firefox')) {
    deviceName = 'Firefox';
  } else if (ua.includes('Safari')) {
    deviceName = 'Safari';
  } else if (ua.includes('Edge')) {
    deviceName = 'Edge';
  }

  // Detect OS
  if (ua.includes('Windows')) {
    deviceName += ' on Windows';
  } else if (ua.includes('Mac')) {
    deviceName += ' on Mac';
  } else if (ua.includes('Linux')) {
    deviceName += ' on Linux';
  } else if (ua.includes('Android')) {
    deviceName += ' on Android';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    deviceName += ' on iOS';
  }

  return deviceName;
};

export { getDeviceFingerprint, getDeviceName };
export default getDeviceFingerprint;
